/**
 * /dashboard/therapy — THE central therapy hub for everyone (clients, therapists, admins).
 *
 * Single primary CTA: "+ New session" (smart modal).
 * Therapists/Admins also get: "Start instant session now" (ad-hoc room, magic link).
 * Each session row has ONE smart unified button — label adapts to gating state.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Calendar, Clock, FileText, Heart, Plus, Trash2, ArrowLeft, Save, ChevronRight,
  Video, Sparkles, Zap, Copy, X, Search, ExternalLink, ShieldCheck, CreditCard,
} from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { TherapistToday } from "../../components/therapy/TherapistToday";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function fmtMin(m) {
  const h = Math.floor(m / 60), mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}
function parseHHMM(s) {
  const [h, m] = (s || "").split(":").map((x) => parseInt(x, 10) || 0);
  return h * 60 + m;
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
function minutesUntil(iso) {
  if (!iso) return Infinity;
  return Math.round((new Date(iso).getTime() - Date.now()) / 60000);
}

/* ============== /dashboard/therapy — central hub ============== */
export function MyTherapyPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showInstant, setShowInstant] = useState(false);

  async function load() {
    try {
      const r = await api.get("/therapy/sessions/me");
      setRows(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { load(); }, []);

  const isTherapist = user?.role === "therapist" || user?.role === "admin";
  const closed = (s) => ["cancelled", "no_show", "completed"].includes(s.status);
  const upcoming = rows.filter((s) => !closed(s) && minutesUntil(s.starts_at) > -120).sort((a, b) => (a.starts_at || "").localeCompare(b.starts_at || ""));
  const past = rows.filter((s) => closed(s) || minutesUntil(s.starts_at) <= -120);

  return (
    <div data-testid="my-therapy-page" className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Therapy · Your hub</p>
          <h1 className="font-serif font-light text-4xl">Sessions</h1>
          <p className="text-[var(--muted)] text-sm mt-2">
            One place to schedule, join, and follow up on therapy.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isTherapist && (
            <button onClick={() => setShowInstant(true)} className="text-sm px-4 py-3 rounded-xl border border-[#C8A96B]/40 hover:bg-[#C8A96B]/10 flex items-center gap-2"
              data-testid="therapy-start-instant-btn">
              <Zap className="w-4 h-4 text-[var(--gold)]" /> Start instant session
            </button>
          )}
          <button onClick={() => setShowSchedule(true)} className="btn-gold text-sm" data-testid="therapy-new-session-btn">
            <Plus className="w-4 h-4" /> New session
          </button>
        </div>
      </div>

      {isTherapist && (
        <div className="bg-white border border-[var(--line)] rounded-2xl p-5 flex items-center justify-between flex-wrap gap-3"
          data-testid="therapy-therapist-tools">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-[var(--gold)]" />
            <div>
              <p className="font-serif text-lg">Therapist tools</p>
              <p className="text-xs text-black/50">Manage availability, view notes, brand the room.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to="/dashboard/therapy/availability" className="text-sm px-4 py-2 rounded-lg border border-[var(--line)] hover:bg-[#C8A96B]/10"
              data-testid="my-therapy-availability-link">
              Availability
            </Link>
          </div>
        </div>
      )}

      {isTherapist && <TherapistToday onChanged={load} />}

      {/* Upcoming */}
      <Section title="Upcoming" empty="No upcoming sessions." rows={upcoming} render={(s) => (
        <SessionCard key={s.session_id} s={s} user={user} onChange={load} />
      )} />

      {/* Past */}
      {past.length > 0 && (
        <Section title="Past" empty="" rows={past} render={(s) => (
          <SessionCard key={s.session_id} s={s} user={user} onChange={load} muted />
        )} />
      )}

      {/* Quick-find therapist */}
      {!isTherapist && upcoming.length === 0 && (
        <div className="bg-white border border-dashed border-[var(--line)] rounded-2xl p-12 text-center" data-testid="therapy-empty-cta">
          <Heart className="w-10 h-10 mx-auto text-[var(--gold)] mb-3" />
          <p className="text-sm text-black/60 mb-4">Ready to start? Find a therapist that fits.</p>
          <Link to="/therapy" className="btn-gold inline-flex">Browse therapists</Link>
        </div>
      )}

      {showSchedule && <ScheduleModal user={user} onClose={() => { setShowSchedule(false); load(); }} />}
      {showInstant && <InstantModal user={user} onClose={() => { setShowInstant(false); load(); }} />}
    </div>
  );
}

function Section({ title, empty, rows, render }) {
  return (
    <div>
      <h3 className="font-serif text-2xl mb-3">{title}</h3>
      {rows.length === 0 ? (
        empty ? <p className="text-sm text-black/40">{empty}</p> : null
      ) : (
        <div className="space-y-2">{rows.map(render)}</div>
      )}
    </div>
  );
}

/** ONE SMART BUTTON — adapts label + action to session state.
 *  HOST (therapist/admin) can always START the session at any time.
 *  CLIENT sees timed gating: Pay → Intake → "Starts in Xh" → Join now.
 */
function SessionCard({ s, user, onChange, muted }) {
  const isAdmin = user.role === "admin";
  const youAreTherapist = user.user_id === s.therapist_user_id || isAdmin;
  const youAreClient = user.user_id === s.client_user_id;
  const youAreHost = youAreTherapist; // host = the one who runs the session
  const other = youAreTherapist
    ? (s.client_name || s.client_email)
    : s.therapist_name;
  const minsUntil = minutesUntil(s.starts_at);
  const isLive = minsUntil <= 5 && minsUntil >= -120;
  const cancelled = s.status === "cancelled";
  const completed = minsUntil < -10 && !cancelled;
  const needsPay = youAreClient && !youAreHost && !["paid", "complimentary"].includes(s.payment_status);
  const needsIntake = youAreClient && !youAreHost && !s.intake_completed;

  let primaryLabel, primaryHref, primaryIcon, primaryStyle = "btn-gold";
  if (cancelled) {
    primaryLabel = "Cancelled";
    primaryStyle = "px-4 py-2 rounded-xl bg-black/10 text-black/50 text-sm cursor-not-allowed pointer-events-none";
    primaryIcon = <X className="w-4 h-4" />;
    primaryHref = null;
  } else if (completed) {
    primaryLabel = youAreClient && !isAdmin ? "Rate session" : "View notes";
    primaryHref = youAreClient && !isAdmin
      ? `/therapy/feedback/${s.session_id}`
      : `/dashboard/therapy/notes/${s.session_id}`;
    primaryIcon = youAreClient && !isAdmin ? <Sparkles className="w-4 h-4" /> : <FileText className="w-4 h-4" />;
    primaryStyle = "text-sm px-4 py-2 rounded-xl border border-[var(--line)] hover:bg-[#C8A96B]/10 flex items-center gap-2";
  } else if (youAreHost) {
    // HOST: always able to start the session, no payment/intake gate
    primaryLabel = isLive ? "Start session" : "Start session now";
    primaryHref = `/therapy/session/${s.session_id}`;
    primaryIcon = <Video className="w-4 h-4" />;
    primaryStyle = isLive ? "btn-gold" : "btn-gold";
  } else if (needsPay) {
    primaryLabel = `Pay $${(s.amount || 0).toFixed(2)} & continue`;
    primaryHref = `/therapy/session/${s.session_id}`;
    primaryIcon = <CreditCard className="w-4 h-4" />;
  } else if (needsIntake) {
    primaryLabel = "Complete intake";
    primaryHref = `/therapy/intake/${s.session_id}`;
    primaryIcon = <ShieldCheck className="w-4 h-4" />;
  } else if (isLive) {
    primaryLabel = "Join now";
    primaryHref = `/therapy/session/${s.session_id}`;
    primaryIcon = <Video className="w-4 h-4" />;
  } else if (minsUntil > 0) {
    const h = Math.floor(minsUntil / 60), m = minsUntil % 60;
    const when = h > 24 ? `in ${Math.floor(h / 24)}d` : h ? `in ${h}h ${m}m` : `in ${m}m`;
    primaryLabel = `Starts ${when}`;
    primaryHref = `/therapy/session/${s.session_id}`;
    primaryIcon = <Clock className="w-4 h-4" />;
    primaryStyle = "text-sm px-4 py-2 rounded-xl border border-[#C8A96B]/40 hover:bg-[#C8A96B]/10 flex items-center gap-2";
  } else {
    primaryLabel = "Open";
    primaryHref = `/therapy/session/${s.session_id}`;
    primaryIcon = <ChevronRight className="w-4 h-4" />;
  }

  async function copyLink() {
    const url = `${window.location.origin}/therapy/session/${s.session_id}`;
    await navigator.clipboard.writeText(url);
    toast.success("Magic link copied");
  }
  async function cancel() {
    if (!window.confirm("Cancel this session?")) return;
    try { await api.post(`/therapy/sessions/${s.session_id}/cancel`); toast.success("Cancelled"); onChange?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className={`bg-white border rounded-xl p-4 ${muted ? "border-[var(--line)] opacity-70" : isLive ? "border-[var(--gold)] shadow-md" : "border-[var(--line)]"}`}
      data-testid={`therapy-session-${s.session_id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isLive ? "bg-[var(--gold)] text-white animate-pulse" : "bg-[#C8A96B]/15 text-[var(--gold)]"}`}>
          {isLive ? <Video className="w-5 h-5" /> : <Calendar className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-[180px]">
          <p className="font-serif text-lg">{fmtDateTime(s.starts_at)}</p>
          <p className="text-xs text-black/50">
            {youAreTherapist ? "with " : "with "} <strong>{other}</strong> · {s.duration_minutes} min
            {s.amount > 0 && <> · ${s.amount.toFixed(2)}</>}
            {s.instant && <> · <span className="text-[var(--gold)]">instant</span></>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {primaryHref ? (
            <Link to={primaryHref} className={primaryStyle.includes("btn-gold") ? primaryStyle : primaryStyle}
              data-testid={`therapy-session-cta-${s.session_id}`}>
              {primaryIcon} {primaryLabel}
            </Link>
          ) : (
            <span className={primaryStyle}>{primaryIcon} {primaryLabel}</span>
          )}
          {/* Therapist-only secondary buttons */}
          {youAreTherapist && !cancelled && (
            <>
              <button onClick={copyLink} className="text-xs p-2 rounded-lg border border-[var(--line)] hover:bg-[#C8A96B]/10"
                title="Copy magic link" data-testid={`therapy-copy-link-${s.session_id}`}>
                <Copy className="w-3.5 h-3.5" />
              </button>
              {!completed && (
                <Link to={`/dashboard/therapy/notes/${s.session_id}`}
                  className="text-xs p-2 rounded-lg border border-[var(--line)] hover:bg-[#C8A96B]/10"
                  title="Notes" data-testid={`therapy-notes-link-${s.session_id}`}>
                  <FileText className="w-3.5 h-3.5" />
                </Link>
              )}
              {!isLive && !completed && (
                <button onClick={cancel} className="text-xs p-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                  title="Cancel" data-testid={`therapy-cancel-${s.session_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============== Schedule modal — works for client + therapist + admin ============== */
function ScheduleModal({ user, onClose }) {
  const [therapists, setTherapists] = useState([]);
  const [clientLookup, setClientLookup] = useState([]);
  const [pickedTherapist, setPickedTherapist] = useState(null);
  const [pickedClient, setPickedClient] = useState(null);
  const [clientQuery, setClientQuery] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [duration, setDuration] = useState(50);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const isTherapistRole = user?.role === "therapist" || user?.role === "admin";

  useEffect(() => {
    if (!isTherapistRole) {
      api.get("/therapy/therapists").then((r) => setTherapists(r.data || [])).catch(() => null);
    } else if (user?.role === "admin") {
      api.get("/admin/users").then((r) => setClientLookup(r.data || [])).catch(() => null);
    }
  }, [isTherapistRole, user]);

  const filteredClients = useMemo(() => {
    if (!clientQuery.trim()) return clientLookup.slice(0, 10);
    const q = clientQuery.toLowerCase();
    return clientLookup.filter((u) =>
      (u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q)
    ).slice(0, 10);
  }, [clientQuery, clientLookup]);

  async function submit() {
    if (!date || !time) return toast.error("Pick a date and time.");
    const startsAt = new Date(`${date}T${time}:00`).toISOString();
    setBusy(true);
    try {
      if (isTherapistRole) {
        // Therapist/admin schedules with a client
        if (!pickedClient) return toast.error("Pick a client.");
        const therapistId = user.role === "admin" && pickedTherapist ? pickedTherapist.user_id : user.user_id;
        await api.post("/admin/therapy/sessions", {
          therapist_user_id: therapistId,
          client_user_id: pickedClient.user_id,
          starts_at: startsAt,
          duration_minutes: Number(duration),
        });
        toast.success("Session scheduled.");
      } else {
        // Client books with a therapist
        if (!pickedTherapist) return toast.error("Pick a therapist.");
        await api.post("/therapy/book", {
          therapist_user_id: pickedTherapist.user_id,
          starts_at: startsAt,
          duration_minutes: Number(duration),
        });
        toast.success("Session booked. Complete intake & payment to confirm.");
      }
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="therapy-schedule-modal">
      <div className="bg-white rounded-2xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-3xl">Schedule a session</h2>
          <button onClick={onClose} className="text-black/40 hover:text-black"><X className="w-5 h-5" /></button>
        </div>

        {!isTherapistRole && (
          <div className="mb-4">
            <label className="text-[11px] uppercase tracking-widest text-black/50">Therapist</label>
            <select value={pickedTherapist?.user_id || ""} onChange={(e) => setPickedTherapist(therapists.find((t) => t.user_id === e.target.value))}
              className="w-full mt-1 px-4 py-3 rounded-xl border border-[var(--line)]" data-testid="schedule-therapist-select">
              <option value="">Choose a therapist…</option>
              {therapists.map((t) => <option key={t.user_id} value={t.user_id}>{t.name} — ${t.hourly_rate}/hr</option>)}
            </select>
            {pickedTherapist && <p className="text-xs text-black/50 mt-2">{pickedTherapist.bio?.slice(0, 140)}…</p>}
          </div>
        )}

        {isTherapistRole && (
          <div className="mb-4">
            <label className="text-[11px] uppercase tracking-widest text-black/50">Client (existing platform user)</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-black/30" />
              <input value={clientQuery} onChange={(e) => { setClientQuery(e.target.value); setPickedClient(null); }}
                placeholder="Search by name or email…" className="w-full pl-10 px-4 py-3 rounded-xl border border-[var(--line)]"
                data-testid="schedule-client-search" />
            </div>
            {!pickedClient && clientQuery && (
              <div className="mt-1 max-h-48 overflow-y-auto border border-[var(--line)] rounded-xl divide-y divide-[var(--line)]">
                {filteredClients.map((u) => (
                  <button key={u.user_id} onClick={() => { setPickedClient(u); setClientQuery(`${u.name} <${u.email}>`); }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-[#C8A96B]/10"
                    data-testid={`schedule-client-pick-${u.user_id}`}>
                    {u.name} <span className="text-black/40">{u.email}</span>
                  </button>
                ))}
                {filteredClients.length === 0 && <p className="px-4 py-3 text-xs text-black/40">No match — they need an account first.</p>}
              </div>
            )}
            {pickedClient && (
              <p className="text-xs text-emerald-700 mt-1">✓ {pickedClient.name} ({pickedClient.email})</p>
            )}
          </div>
        )}

        <div className="grid grid-cols-3 gap-3 mb-4">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-black/50">Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="w-full mt-1 px-3 py-3 rounded-xl border border-[var(--line)]" data-testid="schedule-date" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-black/50">Time</label>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
              className="w-full mt-1 px-3 py-3 rounded-xl border border-[var(--line)]" data-testid="schedule-time" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-black/50">Duration</label>
            <select value={duration} onChange={(e) => setDuration(e.target.value)}
              className="w-full mt-1 px-3 py-3 rounded-xl border border-[var(--line)]" data-testid="schedule-duration">
              <option value={30}>30 min</option>
              <option value={50}>50 min</option>
              <option value={90}>90 min</option>
            </select>
          </div>
        </div>

        <button onClick={submit} disabled={busy} className="btn-gold w-full" data-testid="schedule-submit">
          <Calendar className="w-4 h-4" /> {busy ? "Saving…" : "Schedule session"}
        </button>
      </div>
    </div>
  );
}

/* ============== Instant session modal (therapist/admin) ============== */
function InstantModal({ user, onClose }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [duration, setDuration] = useState(50);
  const [skipIntake, setSkipIntake] = useState(true);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function start() {
    if (!email.trim() || !email.includes("@")) return toast.error("Client email required.");
    setBusy(true);
    try {
      const r = await api.post("/therapy/instant", {
        client_email: email.trim(), client_name: name.trim() || undefined,
        duration_minutes: Number(duration), skip_intake: skipIntake,
      });
      setCreated(r.data);
      toast.success("Instant session created.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  function copyLink() {
    if (!created) return;
    const url = `${window.location.origin}${created.magic_link_path}`;
    navigator.clipboard.writeText(url);
    toast.success("Magic link copied");
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="therapy-instant-modal">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-serif text-3xl flex items-center gap-2">
            <Zap className="w-6 h-6 text-[var(--gold)]" /> Start now
          </h2>
          <button onClick={onClose} className="text-black/40"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-black/60 mb-4">Spin up a room immediately. We'll give you a magic link to share.</p>

        {created ? (
          <div className="space-y-4" data-testid="therapy-instant-created">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <p className="text-sm font-medium">Room ready ✓</p>
              <p className="text-xs text-black/60 mt-1">Send this link to your client now:</p>
              <code className="block bg-white px-3 py-2 rounded-lg text-xs mt-2 break-all border border-[var(--line)]">
                {window.location.origin}{created.magic_link_path}
              </code>
            </div>
            <div className="flex gap-2">
              <button onClick={copyLink} className="flex-1 text-sm px-4 py-3 rounded-xl border border-[var(--line)] hover:bg-[#C8A96B]/10"
                data-testid="instant-copy-link"><Copy className="w-4 h-4 inline mr-1" /> Copy link</button>
              <Link to={created.magic_link_path} className="btn-gold flex-1" data-testid="instant-enter">
                <Video className="w-4 h-4" /> Enter room
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <Inp label="Client email *" v={email} on={setEmail} placeholder="client@email.com" testid="instant-email" />
              <Inp label="Client name" v={name} on={setName} placeholder="Optional" testid="instant-name" />
              <div>
                <label className="text-[11px] uppercase tracking-widest text-black/50">Duration</label>
                <select value={duration} onChange={(e) => setDuration(e.target.value)}
                  className="w-full mt-1 px-4 py-3 rounded-xl border border-[var(--line)]" data-testid="instant-duration">
                  <option value={30}>30 min</option>
                  <option value={50}>50 min</option>
                  <option value={90}>90 min</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm" data-testid="instant-skip-intake">
                <input type="checkbox" checked={skipIntake} onChange={(e) => setSkipIntake(e.target.checked)} className="accent-[var(--gold)]" />
                Skip intake form (impromptu / returning client)
              </label>
            </div>
            <button onClick={start} disabled={busy} className="btn-gold w-full mt-4" data-testid="instant-submit">
              <Zap className="w-4 h-4" /> {busy ? "Creating…" : "Create instant room"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function Inp({ label, v, on, placeholder, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-black/50">{label}</label>
      <input value={v} onChange={(e) => on(e.target.value)} placeholder={placeholder}
        className="w-full mt-1 px-4 py-3 rounded-xl border border-[var(--line)] outline-none focus:border-[var(--gold)]"
        data-testid={testid} />
    </div>
  );
}

/* ============== /dashboard/therapy/availability — unchanged ============== */
export function TherapistAvailabilityPage() {
  const { user, loading } = useAuth();
  const [rows, setRows] = useState([]);
  const [weekday, setWeekday] = useState(0);
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("17:00");

  async function load() {
    try { const r = await api.get("/therapy/availability/me"); setRows(r.data || []); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { if (user) load(); }, [user]);

  if (loading) return null;
  if (!user || (user.role !== "therapist" && user.role !== "admin")) {
    return <Navigate to="/dashboard" replace />;
  }

  async function add() {
    const sm = parseHHMM(start), em = parseHHMM(end);
    if (em <= sm) return toast.error("End must be after start.");
    try {
      await api.post("/therapy/availability", { weekday: Number(weekday), start_minutes: sm, end_minutes: em });
      toast.success("Window added.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function remove(slot_id) {
    try { await api.delete(`/therapy/availability/${slot_id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div data-testid="therapist-availability-page" className="space-y-6 max-w-2xl">
      <Link to="/dashboard/therapy" className="text-sm text-black/50 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Back</Link>
      <div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Therapist · Availability</p>
        <h1 className="font-serif font-light text-4xl">Weekly hours</h1>
        <p className="text-[var(--muted)] text-sm mt-2">Times are in UTC for now. Clients can book inside these windows.</p>
      </div>

      <div className="bg-white border border-[var(--line)] rounded-2xl p-6">
        <h3 className="font-serif text-2xl mb-4 flex items-center gap-2"><Plus className="w-5 h-5 text-[var(--gold)]" /> Add window</h3>
        <div className="grid sm:grid-cols-4 gap-3 mb-4">
          <select value={weekday} onChange={(e) => setWeekday(e.target.value)}
            className="px-4 py-3 rounded-xl border border-[var(--line)]" data-testid="avail-weekday">
            {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
          </select>
          <input type="time" value={start} onChange={(e) => setStart(e.target.value)} className="px-4 py-3 rounded-xl border border-[var(--line)]" data-testid="avail-start" />
          <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="px-4 py-3 rounded-xl border border-[var(--line)]" data-testid="avail-end" />
          <button onClick={add} className="btn-gold" data-testid="avail-add">Add</button>
        </div>
      </div>

      <div className="space-y-2" data-testid="avail-list">
        {rows.length === 0 ? (
          <p className="text-sm text-black/40 text-center py-6">No windows yet.</p>
        ) : rows.map((r) => (
          <div key={r.slot_id} className="bg-white border border-[var(--line)] rounded-xl p-4 flex items-center gap-3"
            data-testid={`avail-row-${r.slot_id}`}>
            <Clock className="w-4 h-4 text-[var(--gold)]" />
            <div className="flex-1">
              <p className="font-medium">{WEEKDAYS[r.weekday]} · {fmtMin(r.start_minutes)} – {fmtMin(r.end_minutes)}</p>
            </div>
            <button onClick={() => remove(r.slot_id)} className="text-red-600 text-xs px-3 py-1.5 rounded-lg border border-red-200 hover:bg-red-50"
              data-testid={`avail-remove-${r.slot_id}`}>
              <Trash2 className="w-3 h-3 inline" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============== /dashboard/therapy/notes/:id — unchanged ============== */
export function TherapistNotesPage() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const [notes, setNotes] = useState("");
  const [intake, setIntake] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get(`/therapy/sessions/${id}/notes`)
      .then((r) => { setNotes(r.data.notes || ""); setIntake(r.data.intake || {}); })
      .catch((e) => toast.error(formatApiError(e)));
  }, [id, user]);

  if (loading) return null;
  if (!user || (user.role !== "therapist" && user.role !== "admin")) return <Navigate to="/dashboard" replace />;

  async function save() {
    setSaving(true);
    try { await api.post(`/therapy/sessions/${id}/notes`, { notes }); toast.success("Saved."); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <div data-testid="therapist-notes-page" className="space-y-6 max-w-3xl">
      <Link to="/dashboard/therapy" className="text-sm text-black/50 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Back</Link>
      <div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Therapist · Private</p>
        <h1 className="font-serif font-light text-4xl">Session Notes</h1>
        <p className="text-[var(--muted)] text-sm mt-2">Only you and the platform admin can see these.</p>
      </div>

      {intake.submitted_at && (
        <div className="bg-[#C8A96B]/10 border border-[#C8A96B]/30 rounded-2xl p-6" data-testid="notes-intake-block">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Couples intake (auto-attached)</p>
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <Row k="Your name" v={intake.your_name} />
            <Row k="Partner" v={intake.partner_name} />
            <Row k="Length" v={intake.relationship_length} />
            <Row k="Prior therapy" v={intake.prior_therapy ? "Yes" : "No"} />
            <Row k="Safety concerns" v={intake.safety_concerns ? "Yes — review" : "No"} />
          </div>
          <Row k="Primary concern" v={intake.primary_concern} block />
          <Row k="Goals" v={intake.goals} block />
          {intake.additional && <Row k="Additional" v={intake.additional} block />}
        </div>
      )}

      <div className="bg-white border border-[var(--line)] rounded-2xl p-6">
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={12}
          placeholder="Session observations, plan, follow-ups…"
          className="w-full px-4 py-3 rounded-xl border border-[var(--line)] outline-none focus:border-[var(--gold)] font-mono text-sm"
          data-testid="notes-textarea" />
        <button onClick={save} disabled={saving} className="btn-gold mt-4" data-testid="notes-save">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save notes"}
        </button>
      </div>
    </div>
  );
}

function Row({ k, v, block }) {
  if (!v) return null;
  return (
    <div className={block ? "mt-3" : ""}>
      <p className="text-[10px] uppercase tracking-widest text-black/50">{k}</p>
      <p className="text-sm text-black/80 mt-0.5 whitespace-pre-wrap">{v}</p>
    </div>
  );
}
